import { ReactNode } from 'react'
import { AlertCircle, Check, Circle, MinusCircle, PauseCircle } from 'lucide-react'

import {
  WorkflowRunStep,
  WorkflowRunStepState,
  localizedWorkflowStepTitle,
} from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import i18n from '@/i18n'
import { cn } from '@/lib/utils'
import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'

/**
 * One step of a run, as a row.
 *
 * A running step is built on `ThreadActivityIndicator`'s shape rather than
 * imported from it: the same `TwinsMarkLoader` glyph, the same muted line, the
 * same `role="status" aria-live="polite"`, so a step in flight looks like every
 * other thing this product waits on. The copy differs because that component
 * says what a THREAD is doing (searching your library), and a workflow step is
 * doing its own named work.
 *
 * A fan-out in flight is not a loading state. Two of six drafts having landed
 * is a real frame, so the row says `2 of 6` and skeletonises only the drafts
 * that are genuinely still missing.
 */

const STATE_KEYS: Record<WorkflowRunStepState, string> = {
  queued: 'threads.detail.runPane.queued',
  running: 'threads.detail.runPane.running',
  waiting_external: 'threads.detail.runPane.waiting',
  awaiting_approval: 'threads.detail.runPane.waitingOnYou',
  completed: 'threads.detail.runPane.done',
  failed: 'threads.detail.runPane.failed',
  skipped: 'threads.detail.runPane.skipped',
  cancelled: 'threads.detail.runPane.cancelled',
}

export function runPaneStateLabel(state: WorkflowRunStepState): string {
  return i18n.t(STATE_KEYS[state], { ns: 'app' })
}

const RUN_STATUS_KEYS: Record<string, string> = {
  queued: 'threads.detail.runPane.queued',
  running: 'threads.detail.runPane.running',
  waiting_external: 'threads.detail.runPane.waiting',
  awaiting_approval: 'threads.detail.runPane.waitingOnYou',
  paused: 'threads.detail.runPane.paused',
  completed: 'threads.detail.runPane.done',
  failed: 'threads.detail.runPane.failed',
  cancelled: 'threads.detail.runPane.cancelled',
}

export function runPaneRunStatusLabel(status: string): string {
  const key = RUN_STATUS_KEYS[status]
  return key ? i18n.t(key, { ns: 'app' }) : status
}

interface RunStepRowProps {
  step: WorkflowRunStep
  /** Artifacts this step is expected to produce. 1 unless it fans out. */
  expectedArtifactCount: number
  onRetry: () => void
  /** Seeded template slug for the shared step-title overlay. */
  templateSlug?: string
  /** Thread / answer language for seeded titles. Chrome stays on i18n. */
  language?: string | null
  /** The gate card, when this step is one. */
  children?: ReactNode
}

function landedArtifactCount(step: WorkflowRunStep): number {
  const output = step.outputOverride ?? step.output
  return output && output.kind === 'artifacts' ? output.artifacts.length : 0
}

function StepGlyph(props: { state: WorkflowRunStepState }) {
  const { state } = props

  if (state === 'running') {
    return (
      <TwinsMarkLoader
        className="mt-0.5 h-3.5 w-3.5 text-muted-foreground"
        dataTestId="run-step-mark"
      />
    )
  }

  if (state === 'completed') {
    return (
      <Check
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-chart-positive"
        aria-hidden="true"
      />
    )
  }

  if (state === 'failed') {
    return (
      <AlertCircle
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
        aria-hidden="true"
      />
    )
  }

  if (state === 'awaiting_approval') {
    return (
      <PauseCircle
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground"
        aria-hidden="true"
      />
    )
  }

  if (state === 'skipped' || state === 'cancelled') {
    return (
      <MinusCircle
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    )
  }

  return (
    <Circle
      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50"
      aria-hidden="true"
    />
  )
}

export function RunStepRow(props: RunStepRowProps) {
  const { step, expectedArtifactCount, onRetry, templateSlug, language, children } =
    props
  const title = localizedWorkflowStepTitle({
    templateSlug,
    stepKey: step.stepKey,
    language,
    fallback: step.title,
  })

  const landed = landedArtifactCount(step)
  const isFanningOut =
    step.state === 'running' && expectedArtifactCount > 1 && landed < expectedArtifactCount

  const progress = isFanningOut ? (
    <div className="mt-1.5 space-y-1" data-testid={`run-step-progress-${step.stepIndex}`}>
      <Text size="xs" color="muted">
        {i18n.t('threads.detail.runPane.progressOf', {
          landed,
          expected: expectedArtifactCount,
          ns: 'app',
        })}
      </Text>
      {Array.from({ length: expectedArtifactCount - landed }).map((_unused, offset) => (
        <Skeleton key={offset} className="h-3 w-full rounded" />
      ))}
    </div>
  ) : null

  const failure =
    step.state === 'failed' && step.error ? (
      <div className="mt-1.5 space-y-1.5">
        <Text size="xs" color="muted">
          {step.error.message}
        </Text>
        {step.isRetryable ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={onRetry}
            data-testid={`run-step-retry-${step.stepIndex}`}
          >
            {i18n.t('threads.detail.runPane.retryStep', { ns: 'app' })}
          </Button>
        ) : null}
      </div>
    ) : null

  return (
    <li
      className="px-1 py-2"
      data-testid={`run-step-${step.stepIndex}`}
      data-state={step.state}
      {...(step.state === 'running'
        ? { role: 'status', 'aria-live': 'polite' as const }
        : {})}
    >
      <div className="flex items-start gap-2.5">
        <StepGlyph state={step.state} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <Text
              size="sm"
              className={cn(
                'min-w-0 flex-1 truncate',
                step.state === 'queued' && 'text-muted-foreground',
              )}
            >
              {title}
            </Text>
            <Text size="xs" color="muted" className="shrink-0">
              {runPaneStateLabel(step.state)}
            </Text>
          </div>

          {progress}
          {failure}
          {children}
        </div>
      </div>
    </li>
  )
}
